import {inject, Injectable} from '@angular/core';
import {OnboardingProgress, OnboardingStep, Profile} from '@models';
import {ProfileService} from '../community';

@Injectable({
	providedIn: 'root'
})
export class OnboardingQueryService {
	private readonly profileService = inject(ProfileService);

	getCurrentProfileState(): OnboardingProgress {
		return this.getState(this.profileService.profile());
	}

	getState(profile?: Profile): OnboardingProgress {
		if (!profile) {
			return this.defaultState();
		}
		const existing = profile.getOnboardingProgress();
		const completedSteps = new Set<OnboardingStep>(existing.completedSteps ?? []);

		if (this.isProfileStepComplete(profile)) {
			completedSteps.add(OnboardingStep.Profile);
		}
		if (this.isTestStepComplete(profile)) {
			completedSteps.add(OnboardingStep.Test);
		}
		if (profile.favorits && profile.favorits.length > 0) {
			completedSteps.add(OnboardingStep.FirstInteraction);
		}

		const completedList = this.stepOrder().filter(step => completedSteps.has(step));
		const currentStep = this.resolveCurrentStep(completedList);
		const completedAt = completedList.length === this.stepOrder().length
			? (existing.completedAt ?? Date.now())
			: undefined;

		return {
			...existing,
			currentStep,
			completedSteps: completedList,
			completedAt,
			lastUpdatedAt: existing.lastUpdatedAt ?? Date.now()
		};
	}

	isCompleted(profile?: Profile): boolean {
		return this.getState(profile).completedSteps.length === this.stepOrder().length;
	}

	completionRatio(profile?: Profile): number {
		const completedCount = this.getState(profile).completedSteps.length;
		return completedCount / this.stepOrder().length;
	}

	private resolveCurrentStep(completedSteps: OnboardingStep[]): OnboardingStep {
		for (const step of this.stepOrder()) {
			if (!completedSteps.includes(step)) {
				return step;
			}
		}
		return OnboardingStep.FirstInteraction;
	}

	private defaultState(): OnboardingProgress {
		return {
			currentStep: OnboardingStep.Profile,
			completedSteps: [],
			lastUpdatedAt: Date.now()
		};
	}

	private isProfileStepComplete(profile: Profile): boolean {
		return profile.username !== Profile.defaultUsername && !!profile.username;
	}

	private isTestStepComplete(profile: Profile): boolean {
		return Array.isArray(profile.test) && profile.test.length > 0;
	}

	private stepOrder(): OnboardingStep[] {
		return [OnboardingStep.Profile, OnboardingStep.Test, OnboardingStep.FirstInteraction];
	}
}
